import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, BadgeCheck, Mail, Phone } from "lucide-react";
import { CONTACT, IMAGES } from "../../data/content";

const lineAnim = (i) => ({
  initial: { y: "115%" },
  animate: { y: 0 },
  transition: { duration: 1.1, delay: 0.35 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
});

export const Hero = ({ t }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section id="top" ref={ref} className="relative overflow-hidden bg-[#FAF9F5] pt-28 sm:pt-32">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-40 top-10 h-[560px] w-[560px] rounded-full bg-[#E2EBE5] opacity-70 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-48 bottom-0 h-[420px] w-[420px] rounded-full bg-[#EFECE6] opacity-90 blur-3xl"
      />

      <div className="relative mx-auto grid max-w-7xl gap-12 px-5 pb-16 sm:px-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:pb-24">
        <motion.div style={{ y: textY }}>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="mb-6 text-xs font-semibold uppercase tracking-[0.28em] text-[#556B5C]"
            data-testid="hero-eyebrow"
          >
            {t.hero.eyebrow}
          </motion.p>

          <h1 className="font-display text-[17vw] font-light leading-[0.9] tracking-tight text-[#1E2721] sm:text-7xl lg:text-8xl">
            {t.hero.lines.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span {...lineAnim(i)} className="block">
                  {i === 1 ? <em className="text-[#4D6354]">{line}</em> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85 }}
            className="mt-6 font-display text-xl italic text-[#556B5C] sm:text-2xl"
            data-testid="hero-subtitle"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1 }}
            className="mt-4 max-w-xl text-base leading-relaxed text-[#4A554D]"
          >
            {t.hero.intro}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.12 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <a
              href={`mailto:${CONTACT.email}`}
              data-testid="contact-hero-email-button"
              className="group inline-flex items-center gap-2 rounded-full bg-[#4D6354] px-6 py-3.5 text-sm font-semibold text-[#FAF9F5] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#3E5244] hover:shadow-xl"
            >
              <Mail size={16} className="transition-transform duration-300 group-hover:-rotate-6" />
              {t.hero.ctaEmail}
            </a>
            <a
              href={`tel:+39${CONTACT.phoneTorino}`}
              data-testid="contact-hero-phone-torino"
              className="inline-flex items-center gap-2 rounded-full border border-[#4D6354]/35 px-5 py-3.5 text-sm font-semibold text-[#1E2721] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#4D6354] hover:bg-[#E2EBE5]"
            >
              <Phone size={15} className="text-[#4D6354]" />
              {t.hero.ctaTorino} · {CONTACT.phoneTorinoLabel}
            </a>
            <a
              href={`tel:+39${CONTACT.phoneCarignano}`}
              data-testid="contact-hero-phone-carignano"
              className="inline-flex items-center gap-2 rounded-full border border-[#4D6354]/35 px-5 py-3.5 text-sm font-semibold text-[#1E2721] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#4D6354] hover:bg-[#E2EBE5]"
            >
              <Phone size={15} className="text-[#4D6354]" />
              {t.hero.ctaCarignano} · {CONTACT.phoneCarignanoLabel}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.3 }}
            className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#556B5C]"
          >
            <span data-testid="roi-badge" className="inline-flex items-center gap-1.5">
              <BadgeCheck size={14} className="text-[#C49A5A]" /> ROI
            </span>
            <span data-testid="tsrm-badge" className="inline-flex items-center gap-1.5">
              <BadgeCheck size={14} className="text-[#C49A5A]" /> TSRM
            </span>
          </motion.div>
        </motion.div>

        <motion.div
          style={{ y: imgY }}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto w-full max-w-md lg:max-w-none"
        >
          <div
            aria-hidden="true"
            className="absolute -inset-4 rounded-t-[999px] rounded-b-[2rem] border border-[#C49A5A]/40"
          />
          <div className="relative overflow-hidden rounded-t-[999px] rounded-b-[1.5rem] shadow-2xl shadow-[#4D6354]/20">
            <img
              src={IMAGES.profile}
              alt="Dott.ssa Claudia D'Elia, Osteopata D.O."
              data-testid="hero-profile-photo"
              className="aspect-[4/5] w-full object-cover"
              loading="eager"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#1E2721]/25 via-transparent to-transparent" />
          </div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.4 }}
            className="absolute -bottom-5 left-1/2 w-max -translate-x-1/2 rounded-full border border-[#4D6354]/15 bg-[#FAF9F5]/90 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#38443B] shadow-lg backdrop-blur-md"
          >
            Osteopata D.O. · ROI
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="relative flex justify-center pb-10"
      >
        <span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-[#556B5C]">
          {t.hero.scroll}
          <ArrowDown size={13} className="animate-bounce" />
        </span>
      </motion.div>
    </section>
  );
};
